/**
 * Account-pending sync façade.
 *
 * Bridges the legacy synchronous localStorage API used by 9+ components
 * (RealEstatePage, BudgetPage, AccountPendingDialog, AssetTransactionDialog,
 * StocksPage, CryptoPage, BondsPage, CollectionPage, EpargnePage,
 * LiquiditesPage, …) with the new `/budget/account-pending` backend
 * endpoints, without rewriting each writer.
 *
 * How it works:
 * ─────────────
 *  1. `hydrateAccountPending()` runs at app mount and after every login.
 *     It reads the current backend state and writes it to localStorage in
 *     the legacy nested-map shape so all existing reads still work.
 *  2. The existing writers keep doing `localStorage.setItem(KEY, json)`
 *     followed by a `budget-pending-updated` CustomEvent dispatch.
 *  3. `installAccountPendingAutoSync()` (called once at app bootstrap)
 *     listens for that event, debounces 400 ms, and pushes the current
 *     localStorage snapshot to the backend via `accountPendingApi.sync()`.
 *
 * Silent migration (Q4 contract):
 * ───────────────────────────────
 *  • If `migration_status_patrimony_account_pending_v1` is not 'success',
 *    the very first `hydrateAccountPending()` push the local snapshot to
 *    the backend BEFORE pulling.
 *  • On any failure, localStorage stays intact and the migration flag is
 *    set to 'error' with a reason. The next mount retries.
 *  • On success, the migration flag is set to 'success'.
 *
 * Dirty-flag protection:
 * ─────────────────────
 *  • Every write sets `patrimony_account_pending_dirty=1`.
 *  • Hydration first pushes the dirty snapshot if present, only then pulls
 *    from backend. This prevents losing offline writes when the network
 *    comes back.
 */

import { accountPendingApi } from './api';

const KEY              = 'patrimony_account_pending_v1';
const DIRTY_FLAG       = 'patrimony_account_pending_dirty';
const MIGRATION_FLAG   = 'migration_status_patrimony_account_pending_v1';
const SYNC_DEBOUNCE_MS = 400;

const _readLS = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}'); }
  catch { return {}; }
};
const _writeLS = (data) => {
  try { localStorage.setItem(KEY, JSON.stringify(data || {})); } catch {}
};
const _setDirty = () => { try { localStorage.setItem(DIRTY_FLAG, '1'); } catch {} };
const _clearDirty = () => { try { localStorage.removeItem(DIRTY_FLAG); } catch {} };
const _isDirty = () => {
  try { return localStorage.getItem(DIRTY_FLAG) === '1'; } catch { return false; }
};

const _setMigStatus = (status, extra = {}) => {
  try {
    localStorage.setItem(MIGRATION_FLAG, JSON.stringify({
      ts: Date.now(), status, ...extra,
    }));
    // eslint-disable-next-line no-console
    console.info(`[accountPendingSync] migration status: ${status}`, extra);
  } catch {}
};

const _migrationDone = () => {
  try {
    const s = localStorage.getItem(MIGRATION_FLAG);
    if (!s) return false;
    return JSON.parse(s).status === 'success';
  } catch { return false; }
};

const _flatToNested = (rows) => {
  const nested = {};
  for (const r of rows || []) {
    if (!nested[r.period_key])               nested[r.period_key] = {};
    if (!nested[r.period_key][r.category])   nested[r.period_key][r.category] = {};
    nested[r.period_key][r.category][r.account_id] = {
      amount:    r.amount,
      validated: !!r.validated,
      ...(r.name ? { name: r.name } : {}),
    };
  }
  return nested;
};

/** Push the current localStorage snapshot to the backend. */
const _pushSnapshot = async () => {
  const data = _readLS();
  await accountPendingApi.sync(data);
  _clearDirty();
};

/**
 * Run once at app mount and after every login. Pulls authoritative state
 * from the backend, with an optional first-time migration push if the
 * legacy localStorage data has never been synced.
 */
export const hydrateAccountPending = async () => {
  // First-time migration: push local → backend before doing anything else.
  if (!_migrationDone()) {
    const local = _readLS();
    if (local && Object.keys(local).length > 0) {
      try {
        await accountPendingApi.sync(local);
        // Verify by re-reading; if it comes back empty when we sent data,
        // something is wrong — keep localStorage and retry next mount.
        const r = await accountPendingApi.list();
        if (Array.isArray(r.data) && r.data.length === 0) {
          _setMigStatus('error', { reason: 'verification_empty' });
          return;
        }
        _clearDirty();
        _setMigStatus('success', { migrated: r.data.length });
      } catch (err) {
        _setMigStatus('error', { error: String(err?.message || err) });
        return; // KEEP localStorage, retry next mount
      }
    } else {
      _setMigStatus('success', { migrated: 0, note: 'no_local_data' });
    }
  }

  // If we have unsynced local writes (e.g. offline ones), push them first.
  if (_isDirty()) {
    try { await _pushSnapshot(); }
    catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[accountPendingSync] dirty push failed; keeping local state', err);
      return;
    }
  }

  // Authoritative pull.
  try {
    const r = await accountPendingApi.list();
    _writeLS(_flatToNested(r.data));
    window.dispatchEvent(new CustomEvent('budget-pending-updated', { detail: { source: 'hydrate' } }));
  } catch (err) {
    // Backend down — keep whatever localStorage has, no event dispatch.
    // eslint-disable-next-line no-console
    console.warn('[accountPendingSync] hydrate pull failed', err);
  }
};

let _syncTimer = null;
let _listenerInstalled = false;

/**
 * Subscribe to the global `budget-pending-updated` event so every
 * localStorage write gets mirrored to the backend within ~400 ms. Skips
 * events emitted by `hydrateAccountPending` itself (detail.source==='hydrate')
 * to avoid an infinite loop.
 */
export const installAccountPendingAutoSync = () => {
  if (_listenerInstalled) return;
  _listenerInstalled = true;
  window.addEventListener('budget-pending-updated', (e) => {
    if (e?.detail?.source === 'hydrate') return;
    _setDirty();
    if (_syncTimer) clearTimeout(_syncTimer);
    _syncTimer = setTimeout(async () => {
      try { await _pushSnapshot(); }
      catch (err) {
        // Network blip — flag stays dirty, will retry next event or mount.
        // eslint-disable-next-line no-console
        console.warn('[accountPendingSync] background sync failed', err);
      }
    }, SYNC_DEBOUNCE_MS);
  });
};
